import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { NavigateOptions, To, useNavigate } from 'react-router-dom';
import BackendService from '../services/BaseBackendService';

interface NavigationContextType {
  navigate: (to: To, options?: NavigateOptions & { prefetchRoles?: boolean }) => Promise<void>;
  isNavigating: boolean;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  // Create a stable backend service instance
  const backendService = useMemo(() => {
    return new BackendService();
  }, []);

  const navigateWithRolesCache = useCallback(
    async (to: To, options?: NavigateOptions & { prefetchRoles?: boolean }) => {
      const { prefetchRoles, ...navigateOptions } = options ?? {};

      if (!prefetchRoles) {
        navigate(to, navigateOptions);
        return;
      }

      if (isNavigating) {
        console.debug('Navigation skipped: another navigation is still loading roles');
        return;
      }

      setIsNavigating(true);

      try {
        console.log('Starting navigation with roles cache...');
        // Call setRolesCache before navigation
        await backendService.setRolesCache();
        console.log('Roles cache set successfully, proceeding with navigation');

        // Proceed with navigation
        navigate(to, navigateOptions);
      } catch (error) {
        console.error('Failed to set roles cache before navigation:', error);
        // Still proceed with navigation even if roles cache fails
        navigate(to, navigateOptions);
      } finally {
        setIsNavigating(false);
      }

      return;
    },
    [navigate, backendService, isNavigating],
  );

  const value: NavigationContextType = {
    navigate: navigateWithRolesCache,
    isNavigating,
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    console.error('useNavigation called outside NavigationProvider');
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
